/**
 * The navigation manifest — the single source of truth for what the top nav
 * contains (SAL-2912).
 *
 * Before this file the same routes were written down twice: once as JSX-adjacent
 * arrays inside `components/NavMenu.js`, and again inside
 * `lib/agent-tools/navigation-pages.js` so ⌘K could find them. Keeping the two
 * in step was a rule in CLAUDE.md and a parity test — which is to say, it was
 * someone's memory. Three routes had already fallen out of the search index.
 *
 * Now the menus are declared here, as data, and everything else derives:
 *
 *   • `components/NavMenu.js` renders these sections.
 *   • `lib/agent-tools/navigation-pages.js` flattens them into the ⌘K index.
 *   • `lib/navigation/nav-search-open.js` maps a menu label to its index
 *     section from the same entries.
 *
 * Adding a nav link means adding an entry here. It should not mean editing a
 * component, and it must not mean editing the search index.
 *
 * Like `lib/plugins/registry.js`, this file is deliberately dependency-free:
 * plain data, icons by name, conditions by name. That is what lets it be
 * imported from client components, server code, route guards and unit tests
 * alike. Anything that needs React, Prisma or the session belongs in
 * `lib/navigation/nav-items.js`, which turns this data into rendered items.
 *
 * @typedef {object} NavManifestItem
 * @property {string} [href]      Destination. Omit for a group — a heading whose
 *   children are the links. May contain `{workspaceId}`, substituted per studio.
 * @property {string} label       Menu wording. Studio module renaming may
 *   override it (see `moduleLabels` below).
 * @property {string} [iconName]  Key into components/Sidebar/IconMapping.
 * @property {boolean} [external] Opens off-site; rendered as a plain anchor and
 *   never indexed for search.
 * @property {NavRequirement} [requires]  When this entry is visible. Absent
 *   means always. A group is additionally hidden when nothing under it survives,
 *   so a heading never renders above an empty list.
 * @property {NavManifestItem[]} [subItems]
 *
 * @typedef {string | string[] | {all?: NavRequirement[], any?: NavRequirement[]}} NavRequirement
 *   A condition name from `NAV_CONDITIONS` in lib/navigation/nav-items.js, an
 *   array of them (all must hold), or an explicit `all`/`any` combination —
 *   given both, both have to hold. A name `NAV_CONDITIONS` does not define
 *   hides the entry, so a typo fails closed rather than opening a gated link.
 *
 * @typedef {object} NavManifestMenu
 * @property {string} key         Stable id, used by code that needs one menu.
 * @property {string} menuLabel   The header dropdown's name.
 * @property {string} section     What the ⌘K index calls this menu. Usually the
 *   same word; "My Results" is indexed as "Results" and "Team Reports" as
 *   "Reports", which is the mapping `nav-search-open.js` needs.
 * @property {string} [subtitle]  Small print under the menu name.
 * @property {NavRequirement} [requires]  When the whole menu is offered.
 * @property {string[]} activePrefixes  Route prefixes that mark this menu as the
 *   current one. Menus may overlap — the most specific match wins, so only one
 *   menu is ever active (`resolveActiveNavKey` in lib/navigation/nav-items.js).
 * @property {string} [moduleLabels]  Which relabelling map applies to items in
 *   this menu, for studios that renamed a module ("dashboard" | "report" |
 *   "admin" | "playbook"). See lib/modules/display-names.js.
 * @property {boolean} [overrideExempt]  Skip the studio's `menuOverride`
 *   allow-list. Plugin filtering still applies.
 * @property {{prompt: string, label: string, href: string, requires?: NavRequirement}} [crossLink]
 *   Footer link to the sibling menu people confuse this one with.
 * @property {NavManifestItem[]} items
 */

import { ADMIN_ITEMS } from "@/lib/navigation/manifest/admin";
import { EXECUTION_ITEMS } from "@/lib/navigation/manifest/execution";
import { LEARN_ITEMS } from "@/lib/navigation/manifest/learn";
import { PRACTICE_ITEMS } from "@/lib/navigation/manifest/practice";
import { REPORT_ITEMS } from "@/lib/navigation/manifest/reports";
import { RESULTS_ITEMS } from "@/lib/navigation/manifest/results";

/** @type {NavManifestMenu[]} */
export const NAV_MANIFEST = [
  {
    key: "learn",
    menuLabel: "Learn",
    section: "Learn",
    activePrefixes: ["/playbook", "/dashboard/learning-paths", "/training"],
    moduleLabels: "playbook",
    items: LEARN_ITEMS,
  },
  {
    key: "practice",
    menuLabel: "Practice",
    section: "Practice",
    activePrefixes: ["/simulator"],
    items: PRACTICE_ITEMS,
  },
  {
    key: "execution",
    menuLabel: "Execution",
    section: "Execution",
    activePrefixes: [
      "/dem-cog/reports",
      "/dashboard/transcript-results",
      "/simulator/on-the-fly",
      "/workspaces/scripts",
    ],
    moduleLabels: "dashboard",
    items: EXECUTION_ITEMS,
  },
  {
    key: "results",
    menuLabel: "My Results",
    section: "Results",
    subtitle: "Your personal scores and progress",
    activePrefixes: ["/dashboard", "/leaderboard"],
    moduleLabels: "dashboard",
    crossLink: {
      prompt: "Looking for team reports?",
      label: "Team Reports",
      href: "/workspaces/reports",
      requires: "adminOrSupervisorOrLeader",
    },
    items: RESULTS_ITEMS,
  },
  {
    key: "reports",
    menuLabel: "Team Reports",
    section: "Reports",
    subtitle: "Team-level reports for managers",
    requires: "adminOrSupervisorOrLeader",
    activePrefixes: ["/workspaces/reports"],
    moduleLabels: "report",
    crossLink: {
      prompt: "Looking for your own results?",
      label: "My Results",
      href: "/dashboard",
    },
    items: REPORT_ITEMS,
  },
  {
    key: "admin",
    menuLabel: "Admin",
    section: "Admin",
    requires: "adminOrSupervisorOrLeader",
    // Every /workspaces route except the reports ones, which belong to the menu
    // above. Overlap is fine: `resolveActiveNavKey` gives a route to the menu
    // with the most specific matching prefix, so "/workspaces/reports/..."
    // highlights Team Reports rather than both menus at once.
    activePrefixes: ["/supervisor", "/workspaces"],
    moduleLabels: "admin",
    // Admins must be able to reach the settings that produce a menuOverride,
    // or a stripped studio cannot be reconfigured from inside the app.
    overrideExempt: true,
    items: ADMIN_ITEMS,
  },
];

/**
 * The cut-down header shown to training-mode users, who see no dropdowns at
 * all. Declared here for the same reason as everything else: the ⌘K index has
 * to agree with it.
 */
export const TRAINING_MODE_NAV = {
  section: "Training",
  items: [
    { href: "/training", label: "Home" },
    { href: "/playbook", label: "Playbook" },
    { href: "/dashboard", label: "My Results" },
    { href: "/leaderboard", label: "Leaderboard" },
  ],
};

const BY_KEY = new Map(NAV_MANIFEST.map((menu) => [menu.key, menu]));

/** @returns {NavManifestMenu | null} */
export function getNavMenu(key) {
  return BY_KEY.get(key) || null;
}

/** Header menu label → the section name the ⌘K index uses for it. */
export const NAV_MENU_LABEL_SECTIONS = Object.fromEntries(
  NAV_MANIFEST.map((menu) => [menu.menuLabel, menu.section]),
);
